import { useMemo } from "react";
import { Badge } from "../Badge/Badge";
import { Button } from "../Button/Button";
import { Table } from "../Table/Table";
import { TableColumn } from "../Table/Table.types";
import {
  KioskTransactionHistoryRow,
  KioskTransactionsPagination,
} from "../../types/kioskTransactions.type";
import { formatCurrency } from "../../utils/currencyUtils";
import { formatDate } from "../../utils/dateFormatter";

interface KioskTransactionHistoryTableProps {
  rows: KioskTransactionHistoryRow[];
  pagination: KioskTransactionsPagination;
  isKioskOwner?: boolean;
  loading?: boolean;
  error?: string | null;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const statusToVariant = (status?: string): "success" | "warning" | "error" | "default" => {
  const value = (status || "").toUpperCase();

  if (value === "RECIEVED" || value === "RECEIVED") {
    return "success";
  }

  if (value === "PENDING") {
    return "warning";
  }

  if (value === "INCOMPLETE") {
    return "error";
  }

  return "default";
};

export const KioskTransactionHistoryTable = ({
  rows,
  pagination,
  isKioskOwner = false,
  loading,
  error,
  onPageChange,
  onLimitChange,
}: KioskTransactionHistoryTableProps) => {
  const columns = useMemo<TableColumn<KioskTransactionHistoryRow>[]>(() => {
    const baseColumns: TableColumn<KioskTransactionHistoryRow>[] = [
      {
        id: "createdAt",
        label: "Created At",
        priority: "high",
        format: (value) => (value ? formatDate(value) : "-"),
      },
      { id: "kioskId", label: "Kiosk ID", priority: "high", format: (value) => value || "-" },
      {
        id: "kioskName",
        label: "Kiosk Name",
        priority: "medium",
        format: (value) => value || "-",
      },
      {
        id: "campaignName",
        label: "Campaign",
        priority: "medium",
        format: (value) => value || "-",
      },
      {
        id: "chargedAmountBase",
        label: "Charged (Base)",
        align: "right",
        priority: "high",
        format: (value) => formatCurrency(value, "USD"),
      },
      {
        id: "numberOfUnits",
        label: "Units",
        align: "right",
        priority: "medium",
        format: (value) => (value ?? 0),
      },
    ];

    if (!isKioskOwner) {
      baseColumns.push(
        {
          id: "status",
          label: "Status",
          type: "component",
          priority: "high",
          format: (value) => (
            <Badge label={value || "-"} variant={statusToVariant(value)} size="sm" />
          ),
        },
        {
          id: "error",
          label: "Error",
          priority: "low",
          format: (value) => {
            if (!value) {
              return "-";
            }

            const text = String(value);
            const shortText = text.length > 50 ? `${text.slice(0, 50)}...` : text;
            return <span title={text}>{shortText}</span>;
          },
        },
      );
    }

    return baseColumns;
  }, [isKioskOwner]);

  const visibleRows = rows.filter((row) => {
    const status = (row.status || "").toUpperCase();
    if (status === "PENDING") {
      return false;
    }

    if (isKioskOwner && status === "INCOMPLETE") {
      return false;
    }

    return true;
  });
  const canGoPrev = pagination.page > 1;
  const canGoNext = pagination.page < pagination.totalPages;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Kiosk Transaction History
        </h2>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {visibleRows.length} records on this page
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <Table
        data={visibleRows}
        columns={columns}
        loading={loading}
        emptyMessage="No kiosk transactions found"
        rowKey={(row) => row.id}
      />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Rows per page</span>
          <select
            value={pagination.limit}
            onChange={(event) => onLimitChange(Number(event.target.value))}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {pagination.page} of {Math.max(1, pagination.totalPages)}
          </span>
          <Button
            value="Previous"
            variant="secondary"
            size="sm"
            disabled={!canGoPrev || loading}
            onClick={() => onPageChange(pagination.page - 1)}
          />
          <Button
            value="Next"
            variant="secondary"
            size="sm"
            disabled={!canGoNext || loading}
            onClick={() => onPageChange(pagination.page + 1)}
          />
        </div>
      </div>
    </div>
  );
};

export default KioskTransactionHistoryTable;
